import { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, ExternalLink, ArrowRight } from 'lucide-react';
import { supabase } from '../lib/supabase';
import toast from 'react-hot-toast';
import { ShowcaseProject } from '../types/showcase';

const ProjectDetail = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [project, setProject] = useState<ShowcaseProject | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (id) fetchProject(id);
  }, [id]);

  const fetchProject = async (projectId: string) => {
    try {
      const { data, error } = await supabase
        .from('showcase_projects')
        .select('*')
        .eq('id', projectId)
        .maybeSingle();

      if (error) throw error;
      if (!data) {
        toast.error('Project not found');
        navigate('/showcase');
        return;
      }

      setProject(data);
    } catch (error) {
      console.error('Error fetching project:', error);
      toast.error('Failed to load project');
      navigate('/showcase');
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate('/showcase');
  };

  if (loading) {
    return (
      <div className="min-h-screen">
        <div className="max-w-3xl mx-auto px-6 pt-32 lg:pt-40 pb-20 space-y-8">
          <div className="h-8 bg-white/[0.03] rounded w-24 animate-pulse" />
          <div className="h-10 bg-white/[0.03] rounded w-3/4 animate-pulse" />
          <div className="h-6 bg-white/[0.03] rounded w-1/2 animate-pulse" />
          <div className="space-y-4 pt-8">
            <div className="h-4 bg-white/[0.02] rounded w-full animate-pulse" />
            <div className="h-4 bg-white/[0.02] rounded w-5/6 animate-pulse" />
            <div className="h-4 bg-white/[0.02] rounded w-4/6 animate-pulse" />
          </div>
        </div>
      </div>
    );
  }

  if (!project) return null;

  return (
    <div className="min-h-screen">
      <section className="pt-32 lg:pt-40 pb-16 lg:pb-20 px-6 lg:px-8">
        <div className="max-w-3xl mx-auto">
          <motion.button
            onClick={handleBack}
            initial={{ opacity: 0, x: -15 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2, duration: 0.4 }}
            className="flex items-center gap-2 px-4 py-2.5 rounded-full bg-white/[0.08] border border-white/[0.12] text-white/90 text-sm font-medium hover:bg-white/[0.14] transition-all duration-300 mb-12"
          >
            <ArrowLeft className="h-4 w-4" />
            Back
          </motion.button>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <span className="text-xs text-primary/80 uppercase tracking-[0.2em] font-medium">
              {project.category}
            </span>
            <h1 className="mt-4 text-4xl sm:text-5xl lg:text-7xl font-semibold text-white tracking-[-0.02em] leading-[1.08]">
              {project.title}
            </h1>
            {project.client_name && (
              <p className="mt-4 text-base text-gray-400">
                {project.client_name}
              </p>
            )}
          </motion.div>
        </div>
      </section>

      <motion.section
        initial={{ opacity: 0, y: 25 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.4 }}
        className="max-w-3xl mx-auto px-6 lg:px-8 py-24 lg:py-32"
      >
        <p className="text-2xl sm:text-3xl lg:text-4xl text-gray-200 leading-[1.4] font-light tracking-tight">
          {project.short_description}
        </p>
      </motion.section>

      <div className="max-w-3xl mx-auto px-6 lg:px-8">
        <div className="h-px bg-white/[0.06]" />
      </div>

      <motion.section
        initial={{ opacity: 0, y: 25 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: '-80px' }}
        transition={{ duration: 0.6 }}
        className="max-w-3xl mx-auto px-6 lg:px-8 py-24 lg:py-32"
      >
        <h2 className="text-xs uppercase tracking-[0.2em] text-gray-500 font-medium mb-10">
          About the Project
        </h2>
        <div className="text-base sm:text-lg text-gray-400 leading-[1.85] whitespace-pre-line">
          {project.description}
        </div>
      </motion.section>

      {project.technologies.length > 0 && (
        <motion.section
          initial={{ opacity: 0, y: 25 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 0.6 }}
          className="max-w-3xl mx-auto px-6 lg:px-8 pb-24 lg:pb-32"
        >
          <h2 className="text-xs uppercase tracking-[0.2em] text-gray-500 font-medium mb-8">
            Technologies
          </h2>
          <div className="flex flex-wrap gap-3">
            {project.technologies.map((tech, i) => (
              <motion.span
                key={tech}
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.3, delay: i * 0.04 }}
                className="px-4 py-2 text-sm text-gray-300 bg-white/[0.04] border border-white/[0.08] rounded-lg hover:border-primary/20 hover:bg-primary/[0.04] transition-all duration-300"
              >
                {tech}
              </motion.span>
            ))}
          </div>
        </motion.section>
      )}

      {project.results && (
        <motion.section
          initial={{ opacity: 0, y: 25 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 0.6 }}
          className="max-w-3xl mx-auto px-6 lg:px-8 pb-24 lg:pb-32"
        >
          <h2 className="text-xs uppercase tracking-[0.2em] text-gray-500 font-medium mb-10">
            Results & Impact
          </h2>
          <div className="relative pl-8 border-l-2 border-primary/30">
            <p className="text-lg lg:text-xl text-gray-300 leading-[1.7] whitespace-pre-line font-light">
              {project.results}
            </p>
          </div>
        </motion.section>
      )}

      <section className="max-w-3xl mx-auto px-6 lg:px-8 pb-16 lg:pb-20">
        <div className="border-t border-white/[0.06] pt-12 flex flex-col sm:flex-row items-center justify-between gap-6">
          <button
            onClick={handleBack}
            className="flex items-center gap-2 text-sm text-gray-500 hover:text-white transition-colors duration-300 font-medium"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to all projects
          </button>

          {project.project_url && (
            <a
              href={project.project_url}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-primary inline-flex items-center gap-2 px-7 py-3 text-sm"
            >
              Visit Live Site
              <ExternalLink className="h-4 w-4" />
            </a>
          )}
        </div>
      </section>

      <section className="relative py-24 lg:py-32 overflow-hidden">
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background: 'radial-gradient(ellipse at center bottom, rgba(175,0,255,0.04) 0%, transparent 60%)',
          }}
        />
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="relative max-w-3xl mx-auto px-6 text-center"
        >
          <h2 className="text-2xl lg:text-4xl font-semibold text-white tracking-tight">
            Interested in working together?
          </h2>
          <p className="mt-5 text-gray-400">
            Let's discuss your next project.
          </p>
          <div className="mt-10">
            <Link
              to="/contact"
              className="btn-primary inline-flex items-center gap-2.5 px-8 py-3.5 text-sm"
            >
              Get in Touch
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </motion.div>
      </section>
    </div>
  );
};

export default ProjectDetail;
